<template>
  <article
    v-if="isLoaded"
    class="present-card"
  >
    <!-- Default case -->
    <component
      v-if="type === 'default'"
      :is="anchorType"
      :to="cLink(link)"
      class="present-card__link"
    >
      <div class="present-card__photo-wrap">
        <img
          class="present-card__photo"
          :src="item.photos[this.$store.getters.groupAbb][0].original" :alt="item.name"
        />

        <img
          class="present-card__photo"
          :src="item.photos[this.$store.getters.groupAbb][1].original" :alt="item.name"
        />

        <ul
          v-if="item.labels && item.labels.length > 0"
          class="present-card__labels"
        >
          <li
            v-for="(label, index) in item.labels"
            :key="index"
            class="present-card__label"
          >{{ label }}</li>
        </ul>
      </div>

      <h3 class="present-card__title notranslate">{{ item.name }}</h3>

      <div
        v-if="price"
        class="present-card__footer"
      >
        <Price align="left">
          <template v-slot:brutto>{{ getPriceForGroup(item, $store.getters.getUserGroupId, false) }}</template>
          <template v-slot:netto>{{ getPriceForGroup(item, $store.getters.getUserGroupId, true) }}</template>
        </Price>
      </div>
    </component>
    <!-- /Default case -->

    <!-- Virtual Present -->
    <component
      v-else-if="type === 'virtual'"
      :is="anchorType"
      :to="cLink(link)"
      class="present-card__link"
    >
      <div class="present-card__photo-wrap">
        <PresentPhotoVirtual :products="item.products" />

        <ul
          v-if="item.labels && item.labels.length > 0"
          class="present-card__labels"
        >
          <li
            v-for="(label, index) in item.labels"
            :key="index"
            class="present-card__label"
          >{{ label }}</li>
        </ul>
      </div>

      <h3 class="present-card__title notranslate">{{ item.name }}</h3>

      <div
        v-if="price"
        class="present-card__footer"
      >
        <Price align="left">
          <template v-slot:brutto>{{ humanPrice(item.products.reduce((v1, v2) => v1 + getPriceRawForGroup(v2, $store.getters.getUserGroupId, false), 0) + getPriceBoxRawForGroup($store.getters.getUserGroupId, false)) }}</template>
          <template v-slot:netto>{{ humanPrice(item.products.reduce((v1, v2) => v1 + getPriceRawForGroup(v2, $store.getters.getUserGroupId, true), 0) + splittedTaxSingleRaw(getPriceBoxRawForGroup($store.getters.getUserGroupId, false))) }}</template>
        </Price>
      </div>
    </component>
    <!-- /Virtual Present -->
  </article>

  <!-- placeholder -->
  <div
    v-else
    class="present-card --is-placeholder"
  >
    <div class="present-card__link">
      <div class="present-card__photo-wrap">
        <span class="present-card__photo"></span>
      </div>

      <h3 class="present-card__title"><span></span></h3>

      <div class="present-card__footer">
        <div class="present-card__price">
          <b></b>
          <span></span>
        </div>
      </div>
    </div>
  </div>
  <!-- /placeholder -->
</template>

<script>
import { logicMixin } from '@/mixins/LogicMixin';

import PresentPhotoVirtual from '@/components/Common/PresentPhotoVirtual';
import Price from '@/components/Common/Price';

export default {
  name: 'PresentCard',
  components: {
    PresentPhotoVirtual,
    Price
  },
  props: {
    isLoaded: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'default'
    },
    item: {
      type: Object
    },
    price: {
      type: Boolean,
      default: true
    },
    link: {
      type: String
    }
  },
  mixins: [logicMixin],
  computed: {
    anchorType () {
      return this.link.includes('http') ? 'a' : 'router-link';
    }
  }
};
</script>

<style lang="scss" scoped>
/**
 * Present Card
 *
 * @author juanpablob <m.juanpablob@gmail.com>
 * @desc Present Card for catalog pages
 * @since 2021-03-25
**/

$component: 'present-card';

.#{$component} {
  display: block;
  height: 100% !important;
  border-radius: $border-radius-lg;

  &__link {
    display: block;
    @include padding(map-get($spacers, 2) * 3);
    border-radius: $border-radius-lg;
    box-shadow: 4px 4px 22px rgba(33, 43, 54, 0.06);
    background-color: $white;
    transition: box-shadow 0.1s ease-in-out;
    color: $ink;
  }

  &__photo-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    @include margin-bottom(map-get($spacers, 3));

    @include hover() {
      .#{$component}__photo:nth-child(2) {
        opacity: 1;
      }
    }
  }

  &__photo {
    @include imageRendering;
    display: block;
    width: 100%;
    height: auto;
    object-fit: fill;
    aspect-ratio: 1 / 1;
    transition: all 0.150s ease-in-out;

    // Hover photo
    &:nth-child(2) {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      opacity: 0;
    }
  }

  &__labels {
    @include unlist;
    display: flex;
    position: absolute;
    top: -10px;
    left: 0;
  }

  &__label {
    @include padding((map-get($spacers, 1)) map-get($spacers, 2));
    border-radius: 50px;
    background-color: lighten($yellow, 10%);
    // background-color: $yellow;
    @include font-size($font-size-sm);
    line-height: $line-height-base-sm;

    & + & {
      @include margin-left(map-get($spacers, 2));
    }
  }

  &__title {
    margin: 0;
    height: $font-size-base * 3;
    @include font-size($font-size-base);
    font-family: $font-family-base;
    font-weight: $font-weight-bold;
  }

  &__footer {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  @include hover () {
    box-shadow: 4px 4px 22px rgba(33, 43, 54, 0.130);
  }

  /* Placeholder */
  &.--is-placeholder {
    animation: fading 1.5s infinite;

    .#{$component} {

      &__photo {
        background-color: $sky;
      }

      &__title span {
        display: block;
        width: 70%;
        height: $font-size-base;
        background: $ink-lightest;
      }

      &__price {
        b {
          display: block;
          @include rfs(5rem, width);
          height: $font-size-base;
          background: $ink-lightest;
        }

        span {
          display: block;
          @include rfs(3rem, width);
          height: $font-size-sm;
          @include margin-top(map-get($spacers, 1));
          background: $sky-dark;
        }
      }
    }

    @keyframes fading {
      0% {
      opacity: 1;
      }

      50% {
        opacity: 0.6;
      }

      100% {
        opacity: 1;
      }
    }
  }

  /** RESPONSIVENESS **/

  // ——————————————————————————————
  // Max LG
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-lg')) {
  }

  // ——————————————————————————————
  // Max MD
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-md')) {
  }

  // ——————————————————————————————
  // Max SM
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-sm')) {
  }

  // ——————————————————————————————
  // Max XS
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-xs')) {
    &__photo-wrap {
      @include padding-right(map-get($spacers, 6));
      @include padding-left(map-get($spacers, 6));
    }
  }
}
</style>
